import ParentSize from '@visx/responsive/lib/components/ParentSize'
import {Zoom} from '@visx/zoom'
import {useCallback, useEffect, useRef} from 'react'
import {useSeatHelper} from '../hooks/useSeatHelper'
import {SeatMapEditorProps} from '../SeatMapEditorProps'
import {SeatMapValue} from '../SeatMapValue'
import {SeatValue} from '../SeatValue'
import {SeatAttributes, SVGSeats} from './SVGSeats'
import {ZoomChildrenParam, ZoomArea} from './ZoomArea'

export interface SVGSeatMapInputProps extends SeatMapEditorProps {}

export const SVGSeatMapInput = ({
  columns,
  rows,
  disabled,
  extra,
  shared,
  hidden,
  isBottomToTop,
  isRightToLeft,
  occupied,
  onChange,
  onRowClick,
  renderHidden,
  spacingX,
  value,
}: SVGSeatMapInputProps) => {
  const zoomRef = useRef<ZoomChildrenParam>(null)
  const {getSeatColor, shouldSeatRender} = useSeatHelper({
    renderHidden,
    occupied,
    extra,
    shared,
    hidden,
    disabled,
    value,
  })

  const handleSeatClick = useCallback(
    (seatValue: SeatValue) => {
      const newValue = value?.clone() || new SeatMapValue()
      newValue.toggleValue(seatValue.row, seatValue.column)
      onChange?.(newValue)
    },
    [onChange, value]
  )

  const getSeatAttributes = useCallback(
    (seatValue: SeatValue): Partial<SeatAttributes> => {
      return {
        color: getSeatColor(seatValue.row, seatValue.column),
        hidden: !shouldSeatRender(seatValue.row, seatValue.column),
      }
    },
    [getSeatColor, shouldSeatRender]
  )

  useEffect(() => {
    zoomRef.current?.center()
  }, [rows, columns])

  return (
    <ParentSize>
      {({height, width}) => (
        <Zoom<SVGSVGElement>
          width={width}
          height={height}
          scaleXMin={1 / 5}
          scaleXMax={4}
          scaleYMin={1 / 5}
          scaleYMax={4}
        >
          {(zoom) => (
            <ZoomArea zoomRef={zoomRef} height={height} width={width} zoom={zoom}>
              <SVGSeats
                isBottomToTop={isBottomToTop}
                isRightToLeft={isRightToLeft}
                spacingX={spacingX}
                height={height}
                width={width}
                columns={columns}
                rows={rows}
                onSeatClick={handleSeatClick}
                getSeatAttributes={getSeatAttributes}
              />
            </ZoomArea>
          )}
        </Zoom>
      )}
    </ParentSize>
  )
}
